/**
 * AWS China Client Initialization Utilities
 * Creates and configures AWS SDK v3 clients for China region services
 */

import { getAWSChinaConfig } from "./awsChinaConfig";
import { TranscribeClient } from "@aws-sdk/client-transcribe";
import { S3Client } from "@aws-sdk/client-s3";

/**
 * Configuration options for AWS China clients
 */
export interface ClientConfig {
  maxAttempts?: number;
  requestTimeout?: number;
}

/**
 * Default client configuration for AWS China services
 */
const DEFAULT_CLIENT_CONFIG: ClientConfig = {
  maxAttempts: 3,
  requestTimeout: 30000, // 30 seconds
};

/**
 * Creates a configured Transcribe client for AWS China Beijing region
 * @param config Optional client configuration overrides
 * @returns Configured TranscribeClient instance
 * @throws Error if AWS China configuration is invalid
 */
export const createTranscribeClient = (config: ClientConfig = {}) => {
  const awsConfig = getAWSChinaConfig();
  const clientConfig = { ...DEFAULT_CLIENT_CONFIG, ...config };

  return new TranscribeClient({
    region: awsConfig.region,
    credentials: {
      accessKeyId: awsConfig.accessKeyId,
      secretAccessKey: awsConfig.secretAccessKey,
    },
    endpoint: `https://cn.transcribe.${awsConfig.region}.amazonaws.com.cn`,
    maxAttempts: clientConfig.maxAttempts,
    requestHandler: {
      requestTimeout: clientConfig.requestTimeout,
    },
  });
};

/**
 * Creates a configured S3 client for AWS China Beijing region
 * @param config Optional client configuration overrides
 * @returns Configured S3Client instance
 * @throws Error if AWS China configuration is invalid
 */
export const createS3Client = (config: ClientConfig = {}) => {
  const awsConfig = getAWSChinaConfig();
  const clientConfig = { ...DEFAULT_CLIENT_CONFIG, ...config };

  return new S3Client({
    region: awsConfig.region,
    credentials: {
      accessKeyId: awsConfig.accessKeyId,
      secretAccessKey: awsConfig.secretAccessKey,
    },
    endpoint: `https://s3.${awsConfig.region}.amazonaws.com.cn`,
    maxAttempts: clientConfig.maxAttempts,
    requestHandler: {
      requestTimeout: clientConfig.requestTimeout,
    },
  });
};

/**
 * Validates that AWS China clients can be created successfully
 * @returns Promise that resolves if clients can be created, rejects otherwise
 */
export const validateAwsChinaClients = async (): Promise<void> => {
  try {
    // Test client creation without making actual API calls
    createTranscribeClient();
    createS3Client();
  } catch (error) {
    throw new Error(
      `AWS China client validation failed: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
};
